import { Helmet } from 'react-helmet-async'

/**
 * Componente SEO centralizado — D10.
 *
 * Emite:
 *  - `<title>` único por ruta
 *  - `<meta name="description">` 150-160 chars target
 *  - Open Graph (`og:title`, `og:description`, `og:image`, `og:url`, `og:type`)
 *  - Twitter cards (`summary_large_image`)
 *  - `<link rel="canonical">` si se provee
 *  - JSON-LD `<script type="application/ld+json">` si se provee
 *
 * NO emite `<meta name="robots">` en cada página — eso vive en index.html
 * global hasta D14 productivo (actualmente `noindex, nofollow` temporal).
 * Para páginas que nunca deban indexarse (ej: 404), pasar `noindex`.
 *
 * PHI compliance:
 *  - Nunca incluir `descripcion` generada dinámicamente desde input del
 *    usuario (XSS / data leak). Acá todo es estático.
 *  - `jsonLd` debe ser un objeto plano; se serializa con JSON.stringify
 *    seguro (Helmet escapa `<` y `>`).
 */
export interface SEOProps {
  /** Title final de la tab del navegador. Idealmente <= 60 chars. */
  title: string
  /** Meta description. Target 150-160 chars para SERPs Google. */
  description: string
  /** URL absoluta de la imagen OG (1200x630 recomendado). */
  ogImage?: string
  /** URL absoluta canonical. Si se omite, no se emite canonical. */
  canonical?: string
  /** Objeto JSON-LD Schema.org (MedicalBusiness, Organization, etc). */
  jsonLd?: Record<string, unknown>
  /** Si true, agrega `<meta name="robots" content="noindex">` (404, staging). */
  noindex?: boolean
  /** `og:type`. Default `website`. */
  ogType?: 'website' | 'article' | 'profile'
}

/** Valor por defecto de la imagen OG — hasta que FITLAB provea 1 oficial. */
const DEFAULT_OG_IMAGE = 'https://fitlab.cl/og-default.jpg'

/** URL base canonical del sitio productivo. */
const SITE_ORIGIN = 'https://fitlab.cl'

export default function SEO({
  title,
  description,
  ogImage = DEFAULT_OG_IMAGE,
  canonical,
  jsonLd,
  noindex = false,
  ogType = 'website',
}: SEOProps) {
  const canonicalUrl = canonical ?? SITE_ORIGIN

  return (
    <Helmet>
      {/* Básico */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="FITLAB" />
      <meta property="og:locale" content="es_CL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Schema.org */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  )
}
